import { t } from "i18next";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { TbHistoryToggle } from "react-icons/tb";
import { getDayTimeDiff } from "../../../helpers/utils";
import { convertShiftForDate, fullDayShift } from "../../../lib/calculations";
import { useScreenStore } from "../../../stores/ScreenStore";
import { DateShift, SelectOption } from "../../../types/types";
import { Button } from "../../../ui/Button";
import { Calendar } from "../../../ui/Calendar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../../../ui/DropdownMenu/DropdownMenu";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/Popover";

export const MonitorHistory = () => {
  const [ open, setOpen ] = useState(false);
  const { dateShift, setDateShift, shifts, setIsHistory } = useScreenStore();
  const [ availableShifts, setAvailableShifts ] = useState<DateShift[]>([])
  const [ shiftOptions, setShiftOptions] = useState<SelectOption[]>([])
  const methods = useForm({
    defaultValues: {
      date: dateShift ? dateShift.start_day.toJSDate() : DateTime.local().toJSDate(),
      shift: dateShift ? {value: dateShift.id, label: dateShift.id} : { value: fullDayShift.id, label: fullDayShift.label},
    },
  });
  const { control, watch, handleSubmit, reset, setValue, getValues } = methods;

  const watchDate = watch("date");

  useEffect(() => {
    // Если дата не сегодняшняя - выбор из всех смен
    if (+DateTime.fromJSDate(watchDate).startOf("day") != +DateTime.local().startOf("day")) {
      setAvailableShifts([...shifts!, fullDayShift])
      let screenShifts = availableShifts?.map(s => ({value: String(s.id), label: s.label})) as SelectOption[]
      setShiftOptions(screenShifts);
      setCurrentShiftValue()
      return
    }
    
    const currentDayTime = DateTime.local().toSeconds() - DateTime.local().startOf("day").toSeconds() - 60;

    const shiftDisabled = shifts?.map((shift) => {
      const shiftStartTime = getDayTimeDiff(shift.start_time.toFormat("HH:mm:ss"));
      // Если смена прошла или началась - даём возможность её выбрать
      if(currentDayTime > shiftStartTime) { 
        return {label: shift.label, value: String(shift.id), disable: false} 
      } else { 
        return {label: shift.label, value: String(shift.id), disable: true} 
      }
    });
    setAvailableShifts([...shifts!, fullDayShift])
    setShiftOptions([...shiftDisabled!, {value: fullDayShift.id, label: t(fullDayShift.label)}]);
    setCurrentShiftValue()
  }, [watchDate]);

  function setCurrentShiftValue() {
    if (shiftOptions.map(s => s.value).includes(getValues("shift")?.value)) {
      setValue("shift", shiftOptions.find(s => s.value === getValues("shift")?.value)!)
    } else {
      setValue("shift", shiftOptions.find(s => s.value === "full")!)
    }
  }

  useEffect(() => {
    if (open)
      reset({
        date: dateShift ? dateShift.start_day.toJSDate() : DateTime.local().toJSDate(),
        shift: dateShift ? {value: dateShift.id, label: dateShift.label} : { value: fullDayShift.id, label: fullDayShift.label},
      });
  }, [open]);

  const onSubmit = (formData: any) => {
    setIsHistory(true);
    let ds = convertShiftForDate(availableShifts?.find(s => String(s.id) === formData.shift.value)!, DateTime.fromJSDate(formData.date));
    setDateShift(ds);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <div className="gap-1 flex items-center py-0.5 hover:text-primary rounded-sm cursor-pointer font-medium text-sm">
            <TbHistoryToggle />
            <span>{t('history')}</span>
        </div>
      </PopoverTrigger>
      <PopoverContent align="end">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="text-sm w-full pb-4 px-1">
            <div className="font-semibold">{t('show_history')}</div>
            <div>{t('choose_shift_and_date_history')}</div>
          </div>
          <div className="flex flex-col w-full">
            <Controller
              name="date"
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Calendar
                    className="p-0"
                    initialFocus
                    mode="single"
                    defaultMonth={DateTime.fromJSDate(value).toJSDate()}
                    selected={value}
                    onSelect={onChange}
                    disabled={{
                      after: DateTime.local().toJSDate(),
                    }}
                  />
                );
              }}
            />
            <Controller
              name="shift"
              control={control}
              rules={{ required: true, validate: (value) => value.value.length > 0 || "error message" }}
              render={({ field: { value, onChange } }) => (
                <div className="relative w-full flex flex-col px-1 py-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant={"outline"} size={"datepicker"} className="w-[246px] justify-start">{value.label}</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[246px]">
                        <DropdownMenuLabel>{t("shifts")}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuRadioGroup value={value.value} onValueChange={(value) => onChange(shiftOptions.find(s => s.value == value))}>
                            {shiftOptions.map(shift => (
                              <DropdownMenuRadioItem key={shift.value} disabled={shift.disable} value={shift.value}>{shift.label}</DropdownMenuRadioItem>
                            ))}
                        </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            />
          </div>
          <div className="p-1 flex justify-end">
            <Button type="submit" size="datepicker">{t('load_history')}</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
};

export default MonitorHistory;
